import React, { useState, useEffect } from 'react';
import './Pages.css';

function Admins() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [submittingAdmin, setSubmittingAdmin] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    
    const [newAdmin, setNewAdmin] = useState({
        fullname: '',
        email: '',
        phone: '',
        username: '',
        role: '2' // Default to Policy Manager
    });

    // Get current user from localStorage (consistent with rest of app)
    const currentUser = localStorage.getItem('username');

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            const response = await fetch('https://main.victriedemos.com:3017/api/admins');
            const data = await response.json();
            setAdmins(data);
        } catch (error) {
            console.error('Error fetching admins:', error);
            alert('Failed to fetch admins');
        }
        setLoading(false);
    };

    const handleCreateAdmin = async () => {
        // Validation
        if (!newAdmin.fullname || !newAdmin.email || !newAdmin.phone || !newAdmin.username || !newAdmin.role) {
            alert('All fields are required');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newAdmin.email)) {
            alert('Please enter a valid email address');
            return;
        }

        setSubmittingAdmin(true);
        try {
            const response = await fetch('https://main.victriedemos.com:3017/api/admins', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fullname: newAdmin.fullname,
                    email: newAdmin.email,
                    phone: newAdmin.phone,
                    username: newAdmin.username,
                    role: newAdmin.role,
                    createdBy: currentUser || 'admin'
                }),
            });

            const data = await response.json();
            
            if (data.success) {
                alert('Admin created successfully! Status is set to inactive (0) and password not set.');
                setShowCreateModal(false);
                setNewAdmin({
                    fullname: '',
                    email: '',
                    phone: '',
                    username: '',
                    role: '2'
                });
                fetchAdmins();
            } else {
                alert(data.message || 'Failed to create admin');
            }
        } catch (error) {
            console.error('Error creating admin:', error);
            alert('Failed to create admin');
        }
        setSubmittingAdmin(false);
    };

    const handleViewAdmin = (admin) => {
        setSelectedAdmin(admin);
        setShowViewModal(true);
    };

    const getRoleName = (role) => {
        switch(parseInt(role)) {
            case 1: return 'Administrator';
            case 2: return 'Policy Manager';
            default: return 'Unknown';
        }
    };

    const getStatusBadge = (status) => {
        if (parseInt(status) === 1) {
            return <span className="status-badge status-active">Active</span>;
        } else {
            return <span className="status-badge status-inactive">Inactive</span>;
        }
    };

    if (loading) {
        return <div className="page-container"><div className="loading">Loading admins...</div></div>;
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <h1>Admin Management</h1>
                <button 
                    className="btn btn-primary"
                    onClick={() => setShowCreateModal(true)}
                >
                    + New Admin
                </button>
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Full Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Username</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Date Created</th>
                            <th>Created By</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {admins.length === 0 ? (
                            <tr>
                                <td colSpan="9" style={{ textAlign: 'center' }}>No admins found</td>
                            </tr>
                        ) : (
                            admins.map((admin, index) => (
                                <tr key={index}>
                                    <td>{admin.Fullname}</td>
                                    <td>{admin.Email}</td>
                                    <td>{admin.Phone}</td>
                                    <td>{admin.Username}</td>
                                    <td>
                                        <span className={`role-badge role-${admin.Role}`}>
                                            {getRoleName(admin.Role)}
                                        </span>
                                    </td>
                                    <td>{getStatusBadge(admin.Status)}</td>
                                    <td>{new Date(admin.DateCreated).toLocaleDateString()}</td>
                                    <td>{admin.CreatedBy}</td>
                                    <td>
                                        <button 
                                            className="btn btn-small btn-view"
                                            onClick={() => handleViewAdmin(admin)}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create Admin Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Create New Admin</h2>
                            <button className="close-btn" onClick={() => setShowCreateModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="info-box">
                                <p><strong>Note:</strong> New admin will be created with Status = 0 (Inactive) and no password. 
                                Admin must use "Forgot Password" to set their password and activate their account.</p>
                            </div>
                            
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Full Name <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={newAdmin.fullname}
                                        onChange={(e) => setNewAdmin({...newAdmin, fullname: e.target.value})}
                                        placeholder="Enter full name"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Email <span className="required">*</span></label>
                                    <input
                                        type="email"
                                        value={newAdmin.email}
                                        onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                                        placeholder="Enter email address"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Phone <span className="required">*</span></label>
                                    <input
                                        type="tel"
                                        value={newAdmin.phone}
                                        onChange={(e) => setNewAdmin({...newAdmin, phone: e.target.value})}
                                        placeholder="Enter phone number"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Username <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        value={newAdmin.username}
                                        onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                                        placeholder="Enter username"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Role <span className="required">*</span></label>
                                    <select
                                        value={newAdmin.role}
                                        onChange={(e) => setNewAdmin({...newAdmin, role: e.target.value})}
                                    >
                                        <option value="1">Administrator</option>
                                        <option value="2">Policy Manager</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowCreateModal(false)}
                                disabled={submittingAdmin}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary" 
                                onClick={handleCreateAdmin}
                                disabled={submittingAdmin}
                            >
                                {submittingAdmin ? 'Creating...' : 'Create Admin'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Admin Modal */}
            {showViewModal && selectedAdmin && (
                <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Admin Details</h2>
                            <button className="close-btn" onClick={() => setShowViewModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-section">
                                <h3>Personal Information</h3>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Full Name:</label>
                                        <span>{selectedAdmin.Fullname}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Email:</label>
                                        <span>{selectedAdmin.Email}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Phone:</label>
                                        <span>{selectedAdmin.Phone}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Username:</label>
                                        <span>{selectedAdmin.Username}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h3>Account Information</h3>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Role:</label>
                                        <span>
                                            <span className={`role-badge role-${selectedAdmin.Role}`}>
                                                {getRoleName(selectedAdmin.Role)}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Status:</label>
                                        <span>{getStatusBadge(selectedAdmin.Status)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Date Created:</label>
                                        <span>{new Date(selectedAdmin.DateCreated).toLocaleString()}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Created By:</label>
                                        <span>{selectedAdmin.CreatedBy}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowViewModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Admins;
